"use client";

import { useEffect, useState } from "react";
import { ArrowRight, Play } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ReachYourAcademicPeak() {
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: cardRef,
    isVisible: cardVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [activeStage, setActiveStage] = useState(0);
  const stageItems = [{
    num: "01",
    label: "CALCULUS BC"
  }, {
    num: "02",
    label: "AP PHYSICS"
  }, {
    num: "03",
    label: "SAT MATH"
  }, {
    num: "04",
    label: "STRATEGY"
  }];
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStage(prev => (prev + 1) % stageItems.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [stageItems.length]);
  return <section data-section-id="5236" id="reach-your-academic-peak" className="relative bg-[#0e2058] text-white overflow-hidden min-h-screen flex items-center">
      {}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }} />
      </div>

      {}
      <div className="hidden lg:flex absolute left-8 top-1/2 -translate-y-1/2 flex-col items-center gap-8 z-20">
        <div className="h-32 w-px bg-white/10" />
        <span className="font-mono text-[10px] tracking-[0.3em] vertical-text uppercase opacity-40">
          LAT: 40.7128 | LVL: 01
        </span>
        <div className="h-32 w-px bg-white/10" />
      </div>

      <div className="container mx-auto px-6 lg:px-20 relative z-10 py-24 lg:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          
          {}
          <div ref={contentRef} className={`lg:col-span-7 transition-all duration-1000 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-px bg-[#3c83f6]" />
              <span className="font-mono text-xs font-semibold tracking-[0.2em] uppercase text-[#3c83f6]">
                Elite STEM Tutoring
              </span>
            </div>

            <h1 className="font-sans font-bold leading-[1.1] tracking-tighter mb-8" style={{
            fontSize: "clamp(2.5rem, 8vw, 5.5rem)"
          }}>
              Master the Complex, <br />
              <span className="font-serif italic font-medium text-[#3c83f6] block mt-2">Ascend.</span>
            </h1>

            <p className="font-sans text-lg lg:text-xl text-white/70 max-w-xl mb-12 leading-relaxed">
              ApexTutor provides precision-engineered instruction for Calculus, Physics, and standardized testing, helping students reach their peak academic potential through architectural clarity.
            </p>

            <div className="flex flex-wrap gap-6 items-center">
              <Button asChild size="lg" className="bg-[#3c83f6] text-white hover:bg-[#3c83f6]/90 rounded-sm px-8 h-14 text-sm font-bold tracking-widest uppercase transition-all hover:-translate-y-1 shadow-lg">
                <Link to="/MasteryTracksPage">
                  Download Diagnostic
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>

              <Link to="/TheApexMethodPage" className="group flex items-center gap-3 font-mono text-xs font-bold tracking-widest uppercase text-white hover:text-[#3c83f6] transition-colors">
                <div className="w-10 h-10 flex items-center justify-center border border-white/10 group-hover:border-[#3c83f6] transition-colors">
                  <Play className="w-3 h-3 fill-current" />
                </div>
                View Methodology
              </Link>
            </div>
          </div>

          {}
          <div ref={cardRef} className={`lg:col-span-5 transition-all duration-1000 delay-300 ease-out ${cardVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}>
            <div className="relative">
              {}
              <div className="absolute -left-8 top-0 bottom-0 w-px bg-white/5 hidden lg:block">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#3c83f6] rounded-lg" style={{
                top: `${activeStage / (stageItems.length - 1) * 100}%`,
                transition: 'top 0.5s ease-in-out'
              }} />
              </div>

              <div className="bg-white/[0.02] border border-white/5 p-8 lg:p-12 relative overflow-hidden">
                <div className="relative z-10">
                  <div className="flex justify-between items-end mb-12">
                    <div>
                      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-2">
                        Specialization
                      </span>
                      <h3 className="font-sans text-2xl font-bold tracking-tight">Mastery Tracks</h3>
                    </div>
                    <span className="font-mono text-4xl font-light text-[#3c83f6]">
                      /{stageItems[activeStage].num}
                    </span>
                  </div>

                  <div className="space-y-4">
                    {stageItems.map((item, i) => <div key={item.num} data-index={i} className={`group flex items-center justify-between p-3 -mx-3 transition-all duration-500 cursor-default ${i === activeStage ? "translate-x-2 text-white bg-white/5" : "text-white/30 hover:text-white/60 hover:translate-x-1"}`}>
                        <div className="flex items-center gap-4">
                          <span className="font-mono text-xs">{item.num}</span>
                          <span className="font-sans font-bold tracking-wider text-sm uppercase">{item.label}</span>
                        </div>
                        {i === activeStage && <div className="w-1.5 h-1.5 bg-[#3c83f6]" />}
                      </div>)}
                  </div>

                  <div className="mt-12 pt-8 border-t border-white/5">
                    <p className="font-sans text-sm text-white/50 leading-relaxed">
                      Our curriculum is engineered for the highest level of academic rigor, focusing on first-principles understanding.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4">
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">Scroll</span>
        <div className="w-px h-12 bg-white/10 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1/2 bg-[#3c83f6] animate-scroll-line" />
        </div>
      </div>

      <style>{`
        .vertical-text {
          writing-mode: vertical-rl;
          text-orientation: mixed;
        }
        @keyframes scroll-line {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(200%); }
        }
        .animate-scroll-line {
          animation: scroll-line 2s cubic-bezier(0.65, 0, 0.35, 1) infinite;
        }
      `}</style>
    </section>;
}